// components/ErrorDisplay.js
import React from 'react';
import './ErrorDisplay.css';

// Utility function to convert technical errors into user-friendly messages
const getFriendlyErrorMessage = (error) => {
  if (!error) return null;
  
  const errorStr = error.toLowerCase();
  
  // ============================================
  // USAGE LIMIT ERRORS - CHECK FIRST
  // ============================================
  if (errorStr.includes('limit') || 
      errorStr.includes('exceeded') ||
      errorStr.includes('quota') ||
      errorStr.includes('upgrade') ||
      errorStr.includes('plan limit') ||
      errorStr.includes('monthly limit')) {
    return {
      title: 'Usage Limit Reached',
      message: error, // Show the actual backend message
      type: 'limit'
    };
  }
  
  // Network/Connection errors
  if (errorStr.includes('network') || 
      errorStr.includes('fetch') || 
      errorStr.includes('timeout') ||
      errorStr.includes('connection') ||
      errorStr.includes('500') ||
      errorStr.includes('503') ||
      errorStr.includes('504')) {
    return {
      title: 'Connection Issue',
      message: "We're having trouble connecting to our servers. Please try again.",
      type: 'network'
    };
  }
  
  // Server busy/Rate limiting
  if (errorStr.includes('busy') || 
      errorStr.includes('overload') ||
      errorStr.includes('capacity') ||
      errorStr.includes('rate limit') ||
      errorStr.includes('too many') ||
      errorStr.includes('429')) {
    return {
      title: 'High Demand',
      message: "We're experiencing higher than usual traffic right now. Please try again in a few moments.",
      type: 'server-busy'
    };
  }
  
  // Audio/Video processing failures
  if (errorStr.includes('failed to process') || 
      errorStr.includes('processing error') ||
      errorStr.includes('transcription failed') ||
      errorStr.includes('audio') ||
      errorStr.includes('video') ||
      errorStr.includes('format') ||
      errorStr.includes('codec')) {
    return {
      title: 'Processing Error',
      message: "We couldn't process your file. This might be due to file format or quality issues. Please try a different file or try again later.",
      type: 'processing'
    };
  }
  
  // AI Service errors (hide technical details like Groq, OpenAI, etc.)
  if (errorStr.includes('api') || 
      errorStr.includes('groq') ||
      errorStr.includes('openai') ||
      errorStr.includes('claude') ||
      errorStr.includes('ai service') ||
      errorStr.includes('model')) {
    return {
      title: 'Service Temporarily Unavailable',
      message: "Our slide generation service is temporarily unavailable. We're working to restore it. Please try again in a few minutes.",
      type: 'ai-service'
    };
  }
  
  // File/Upload errors
  if (errorStr.includes('file') ||
      errorStr.includes('upload') ||
      errorStr.includes('size') ||
      errorStr.includes('duration')) {
    return {
      title: 'File Issue',
      message: "There was a problem with your file. Please make sure it meets the format and size requirements, then try again.",
      type: 'file-error'
    };
  }
  
  // Authentication errors
  if (errorStr.includes('auth') ||
      errorStr.includes('unauthorized') ||
      errorStr.includes('401') ||
      errorStr.includes('403')) {
    return {
      title: 'Authentication Required',
      message: "Please sign in to continue using this feature.",
      type: 'auth'
    };
  }
  
  // Default fallback for unknown errors
  return {
    title: 'Something Went Wrong',
    message: "We encountered an unexpected issue. Please try again, and if the problem persists, contact our support team.",
    type: 'unknown'
  };
};

// React Component
function ErrorDisplay({ error }) {
  const errorInfo = getFriendlyErrorMessage(error);
  
  if (!errorInfo) return null;
  
  return (
    <div className="server-error-state-card">
      <div className="server-error-state-content">
        <h3 className="server-error-title">{errorInfo.title}</h3>
        <p className="server-error-message">{errorInfo.message}</p>
      </div>
    </div>
  );
}

export default ErrorDisplay;